import CustomCard from "components/Atoms/CustomCard"
import CustomButton from "components/Atoms/CustomButton"
import CustomCheckbox from "components/Atoms/CustomCheckbox"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomSpinner from "components/Atoms/CustomSpinner"
import CustomTextArea from "components/Atoms/CustomTextArea"
import Divider from "components/Atoms/Divider"
import RichTextEditor from "components/Atoms/RichTextEditor"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FileUpload from "components/Molecules/FileUpload"
import FormController from "components/Molecules/FormController"
import SubHeader from "components/Organisms/SubHeader"
import SearchSelector from "components/Templates/SearchSelector"
import { NOTIFICATION_KINDS } from "data/static.items"
import {
  useCreateUpdateNotificationTemplateNewMutation,
  useNotificationTemplateNewLazyQuery,
} from "generated/graphql"
import {
  ALL_NOTIFICATION_DATA_SOURCES,
  ALL_NOTIFICATION_PROVIDERS,
  ALL_NOTIFICATION_TEMPLATES_V1,
} from "graphql/notification/notification.queries.gql"
import { SEARCH_SELLABLE_ITEMS } from "graphql/selector.queries.gql"
import { invert, isEmpty } from "lodash-es"
import React, { useEffect, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AppToaster from "utils/AppToaster"
import { parseJson } from "utils/custom.utils"
import replaceTemplateVariables from "utils/replace-template-variables"
import useNotificationTemplateStore from "../notification-template.store"
import Outputs from "./Outputs"
import Variables from "./Variables"

export const LINK_TYPES = [
  {
    id: "sellable_item",
    name: "Sellable Item",
  },
]
const NewAddNotificationTemplate = () => {
  const navigate = useNavigate()
  const store = useNotificationTemplateStore()
  const data = store.data
  const { id } = useParams<any>()
  const focused = useRef()
  const kind = data.kind?.id

  const pushNotificationLength = data?.extraFields?.image ? 50 : 434

  const isPushNot = data?.kind?.id === "push"

  const [addUpdate, { loading }] =
    useCreateUpdateNotificationTemplateNewMutation({
      refetchQueries: [
        {
          query: ALL_NOTIFICATION_TEMPLATES_V1,
          variables: {
            search: "",
            first: 10,
            skip: 0,
          },
        },
        {
          query: ALL_NOTIFICATION_TEMPLATES_V1,
        },
      ],
      onCompleted({ addUpdateNotificationTemplateV1: { template } }) {
        if (!parseInt(id)) {
          navigate(`/d/registration/notification-templates/${template.id}`, {
            replace: true,
          })
        }
        AppToaster.show({
          intent: "success",
          message: `${template.name} saved successfully!`,
        })
      },
      onError() {
        AppToaster.show({
          message: "Unable to save the notification template",
          intent: "danger",
        })
      },
    })

  const [getDetails, { loading: dLoading }] =
    useNotificationTemplateNewLazyQuery({
      onCompleted({ v1NotificationTemplateDetail: data }) {
        if (!parseInt(id) || !data) return

        store.initializeState({
          id: data.id,
          title: replaceTemplateVariables(
            data.title,
            invert(data.dataSource?.outputs)
          ),
          name: data.name,
          dataSource: data.dataSource,
          isProgrammatic: data.isProgrammatic,
          provider: data.provider,
          extraFields: {
            ...data.extraFields,
            data: parseJson(data.extraFields.data),
          },
          dataVariables: data.dataVariables,
          providerVariables: isEmpty(data.providerVariables)
            ? []
            : Object.keys(data.providerVariables).map(key => ({
                key: key,
                value: data.providerVariables[key],
              })),
          body: replaceTemplateVariables(
            data.body,
            invert(data.dataSource?.outputs)
          ),
          kind: NOTIFICATION_KINDS.find(
            kind => kind.id === data.provider.kind.toLowerCase()
          ),
        })
      },
    })

  useEffect(() => {
    if (parseInt(id)) {
      getDetails({ variables: { id } })
    }
    return function cleanup() {
      store.resetData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])
  if (dLoading) return <CustomSpinner />

  return (
    <SubHeader
      header={
        <div>
          <CustomButton onClick={store.resetData} className="mr-2">
            Clear
          </CustomButton>
          <CustomButton
            onClick={() => {
              addUpdate({
                variables: {
                  id: data.id,
                  isProgrammatic: data.isProgrammatic,
                  body: replaceTemplateVariables(
                    data.body,
                    data.dataSource?.outputs
                  ),
                  title: replaceTemplateVariables(
                    data.title,
                    data.dataSource?.outputs
                  ),
                  dataSourceId: data.dataSource?.id,
                  extraFields: {
                    ...data.extraFields,
                    data: JSON.stringify(data.extraFields.data),
                  },
                  providerId: data.provider?.id,
                  dataVariables: data.dataVariables,
                  providerVariables: data.providerVariables?.reduce?.(
                    (prev, variable) => {
                      return {
                        ...prev,
                        ...(variable.key && { [variable.key]: variable.value }),
                      }
                    },
                    {}
                  ),
                  name: data.name,
                },
              })
            }}
            loading={loading}
            intent="success"
          >
            Save
          </CustomButton>
        </div>
      }
      breadrumbs={[
        {
          href: "/d/registration/notification-templates",
          text: "Notification Templates",
          onClick(e) {
            e.preventDefault()
            navigate("/d/registration/notification-templates")
          },
        },
        {
          text: id,
          href: `/d/registration/notification-templates/${id}`,
        },
      ]}
    >
      <FormController source={data} onChange={store.setData}>
        <CustomCard className="shadow-sm rounded-md flex p-3">
          <section className="w-7/12 grid grid-cols-3 gap-4 vt-form-grid h-max">
            <StaticSelector
              path="kind"
              label="Sent through"
              items={NOTIFICATION_KINDS}
            />
            <DynamicSelector
              path="provider"
              label="Provider"
              query={ALL_NOTIFICATION_PROVIDERS}
              variables={{
                filter: {
                  kind_In: kind,
                },
              }}
            />
            <div className="flex items-end">
              <CustomCheckbox
                path="isProgrammatic"
                label="Programmatic Access"
              />
            </div>
            <StringInput
              path="name"
              big={false}
              className="col-span-full"
              label="Template Name"
              required
            />
            <StringInput
              disabled={kind === "sms"}
              path="title"
              big={false}
              className="col-span-full"
              label="Message Subject (Optional)"
            />
            <CustomFormGroup label="Body" className="col-span-full">
              {kind === "email" ? (
                <RichTextEditor
                  value={data.body}
                  onEditorChange={a => {
                    store.setData("body", a)
                  }}
                />
              ) : (
                <CustomTextArea
                  rows={8}
                  error={
                    isPushNot && data?.body?.length > pushNotificationLength
                      ? `Message above ${pushNotificationLength} characters may not be rendered completely to the end user (curr: ${
                          data?.body?.length || 0
                        })`
                      : null
                  }
                  fill
                  path="body"
                />
              )}
            </CustomFormGroup>

            {kind === "push" ? (
              <>
                <FileUpload
                  className="mt-[16px] w-max col-span-full"
                  path="extraFields.image"
                  label="Attachments"
                />
                <div className="col-span-full w-max ">
                  <h6>Link To</h6>
                  <div className="flex gap-4">
                    <StaticSelector
                      label="Type"
                      items={LINK_TYPES}
                      path="extraFields.data.action.value.type"
                    />

                    {data.extraFields?.data?.action?.value?.type?.id ===
                      "sellable_item" && (
                      <SearchSelector
                        label="Item"
                        query={SEARCH_SELLABLE_ITEMS}
                        path="extraFields.data.action.value.id"
                      />
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </section>
          <div className="bg-gray-200 w-[1px] mx-3"></div>
          <section className="w-5/12 vt-form-grid">
            <h6 className="mb-2">Provider Related Information</h6>
            <Variables
              list={data?.providerVariables}
              onAdd={value => store.addListItem("providerVariables", value)}
              onRemove={idx => store.removeListItem("providerVariables", idx)}
            />
            <Divider />
            <DynamicSelector
              path="dataSource"
              query={ALL_NOTIFICATION_DATA_SOURCES}
              label="Data Source"
              placeholder="Select module whose data you wish to include in this notification"
            />
            <Outputs focused={focused} />
            {data.isProgrammatic ? (
              <>
                <Divider />
                <h6 className="mb-2">Data Variables</h6>
                {data?.dataSource?.inputs?.map(input => {
                  return (
                    <StringInput
                      fill={false}
                      placeholder="Enter value for the variable"
                      label={
                        <span>
                          Variable - <span className="font-bold">{input}</span>
                        </span>
                      }
                      big={false}
                      path={`dataVariables.${input}`}
                    />
                  )
                }) || "No inputs!"}
              </>
            ) : null}
          </section>
        </CustomCard>
      </FormController>
    </SubHeader>
  )
}

export default NewAddNotificationTemplate
